import * as React from 'react';
import Lottie from 'lottie-react-web';
import animation from '~img/techAnim.json';
import { Image } from '~types';
import {
  Vertical,
  Horizontal,
} from '~components/swace/gatsby-background-image/GatsbyBackgroundImage';
import s from './Hero.module.scss';
import BackgroundImage from '~components/swace/gatsby-background-image';
import Text from '~components/shared/Text';
import Reveal from '~components/swace/reveal';
import Button from '~components/swace/button';
import Section from '~components/shared/Section';
import GatsbyImage from 'gatsby-image';

export interface Props {
  id?: number;
  background?: string;
  overlay?: string;
  title?: string;
  logotype?: Image;
  ingress?: string;
  background_image?: Image;
  show_mute_button?: boolean;
  background_video?: {
    link: string;
  };
  imagePositioning?: {
    vertical: Vertical;
    horizontal: Horizontal;
  };
  button_text?: string;
  button_link?: string;
}

const Background = (props: {
  background: string;
  video?: { link: string };
  videoRef?: React.RefObject<HTMLVideoElement>;
  image?: Image;
  children: any;
  videoMute: boolean;
  showMuteButton: boolean;
  toggleMute: () => void;
  imagePositioning?: {
    vertical: Vertical;
    horizontal: Horizontal;
  };
}) => {
  const {
    background,
    video,
    videoRef,
    videoMute,
    image,
    children,
    imagePositioning,
    toggleMute,
    showMuteButton,
  } = props;
  switch (background) {
    case 'video': {
      return (
        <div className={s.videoWrapper}>
          {showMuteButton && (
            <a className={videoMute ? s.mute : s.unMute} onClick={toggleMute}>
              <img />
            </a>
          )}
          <video
            ref={videoRef}
            autoPlay
            loop
            muted={videoMute}
            className={s.video}
            playsInline
            controls={false}
          >
            <source src={video ? video.link : ''} type="video/mp4" />
          </video>
          {children}
        </div>
      );
    }
    case 'image':
    default: {
      return (
        <BackgroundImage
          image={image}
          fadeIn={false}
          imagePositioning={imagePositioning}
        >
          {children}
        </BackgroundImage>
      );
    }
  }
};

const ButtonRow = (props: {
  buttonText: string;
  buttonLink: string;
  extraMarginTop: boolean;
}) => {
  const { buttonText, buttonLink, extraMarginTop } = props;
  return (
    <div
      className={[
        'row w-100',
        s.buttonContainer,
        extraMarginTop ? s.extraMarginTop : '',
      ].join(' ')}
    >
      <div className="col-12 col-md-10 offset-md-1 d-flex flex-column align-items-center">
        <Reveal lift>
          <Button href={buttonLink}>{buttonText}</Button>
        </Reveal>
      </div>
    </div>
  );
};

const Content = (props: {
  overlay: string;
  title?: string;
  logotype?: Image;
  ingress?: string;
  buttonText?: string;
  buttonLink?: string;
  onClick?: () => void;
}) => {
  const {
    overlay,
    title,
    logotype,
    ingress,
    buttonLink,
    buttonText,
    onClick,
  } = props;
  switch (overlay) {
    case 'text':
      return (
        <div
          className={[
            'container',
            ingress
              ? 'text-left d-flex flex-column justify-content-center align-items-start h-100'
              : 'text-center d-flex flex-column justify-content-center align-items-center h-100',
          ].join(' ')}
          onClick={onClick}
        >
          {title && (
            <div className="row w-100">
              <div
                className={
                  ingress
                    ? 'col-12 col-md-10 offset-md-1'
                    : `col-12 offset-md-1`
                }
              >
                <Reveal fade>
                  <Text h1 left light preLine>
                    {title}
                  </Text>
                </Reveal>
              </div>
            </div>
          )}
          {ingress && (
            <div className="row w-100">
              <div className="col-12 col-md-10 offset-md-1">
                <Reveal fade>
                  <Text ingress light>
                    {ingress}
                  </Text>
                </Reveal>
              </div>
            </div>
          )}
          {buttonText && (
            <ButtonRow
              buttonText={buttonText}
              buttonLink={buttonLink}
              extraMarginTop={!ingress}
            />
          )}
        </div>
      );
    case 'logotype':
      return (
        <div
          className={[
            'container text-center d-flex flex-column justify-content-center align-items-center h-100',
          ].join(' ')}
          onClick={onClick}
        >
          <div className="row">
            <div className="col">
              <Reveal fade>
                <GatsbyImage
                  className={s.logotype}
                  fluid={logotype.localFile.childImageSharp.fluid}
                />
              </Reveal>
            </div>
          </div>
          {buttonText && (
            <ButtonRow
              buttonText={buttonText}
              buttonLink={buttonLink}
              extraMarginTop={!ingress}
            />
          )}
        </div>
      );
    case 'lifestyle_tech':
      return (
        <div className={s.halfHeightWrapper} onClick={onClick}>
          <div className={s.animationWrapper}>
            <Lottie
              options={{
                autoplay: true,
                animationData: animation,
                loop: false,
              }}
              height="100%"
              width="100%"
            />
          </div>
        </div>
      );
    case 'none':
    default:
      return <div className={s.halfHeightWrapper} onClick={onClick} />;
  }
};

class Hero extends React.Component<Props> {
  private videoRef = React.createRef<HTMLVideoElement>();
  state = {
    videoMute: true,
  };

  toggleMute = () => {
    const mute = this.state.videoMute;

    this.setState({
      videoMute: !mute,
    });
  };

  // Function used to start video on click if the video isn't running.
  // This happens on iphones even if autoplay is set if the phone is
  // in battery saving mode. Should be used on click on the overlay or content.
  startVideo = () => {
    const { background_video } = this.props;
    if (!background_video) {
      return;
    }
    const videoElement = this.videoRef.current;
    if (videoElement.currentTime > 0) {
      return;
    }
    videoElement.play();
  };

  render() {
    const {
      background,
      overlay,
      background_image,
      background_video,
      title,
      ingress,
      logotype,
      button_link: buttonLink,
      button_text: buttonText,
      imagePositioning,
      show_mute_button,
    } = this.props;
    const { videoMute } = this.state;
    const isHalfHeight =
      !overlay || overlay === 'lifestyle_tech' || overlay === 'none';

    const none = overlay === 'none';

    return (
      <div className={s.topSection}>
        <Section
          wide
          slimTop
          slimBottom={isHalfHeight || overlay === 'none_full_height'}
        >
          <Background
            background={background}
            video={background_video}
            videoRef={this.videoRef}
            videoMute={videoMute}
            toggleMute={this.toggleMute}
            image={background_image}
            imagePositioning={imagePositioning}
            showMuteButton={show_mute_button}
          >
            <div
              className={
                none
                  ? ''
                  : isHalfHeight
                  ? s.halfHeightWrapper
                  : s.fullHeightWrapper
              }
            >
              <Reveal fade>
                <div className={s.overlay} onClick={this.startVideo} />
              </Reveal>
              <Content
                overlay={overlay}
                title={title}
                ingress={ingress}
                buttonLink={buttonLink}
                buttonText={buttonText}
                logotype={logotype}
                onClick={this.startVideo}
              />
            </div>
          </Background>
        </Section>
      </div>
    );
  }
}

export default Hero;
